import React, { useState } from "react";
import CrudManager from "../../common/CrudManager";
import { Mail, Edit2, Trash2, Copy, Check, Info, Code } from "lucide-react";
import { toast } from "sonner";

const EmailTemplateManager = ({ onClose }) => {
  const [copiedId, setCopiedId] = useState(null);

  const categories = [
    "Confirmation",
    "Interview",
    "Rejection",
    "Offer",
    "Follow-up",
    "Other",
  ];

  const variableHelp = [
    "{{candidateName}}",
    "{{position}}",
    "{{submissionDate}}",
    "{{japaneseLevel}}",
    "{{interviewDate}}",
    "{{interviewTime}}",
  ];

  const getFields = () => [
    {
      name: "name",
      label: "Template Name",
      type: "text",
      placeholder: "e.g., Interview Invitation",
      required: true,
    },
    {
      name: "category",
      label: "Category",
      type: "select",
      required: true,
      options: categories.map((cat) => ({ value: cat, label: cat })),
    },
    {
      name: "subject",
      label: "Email Subject Line",
      type: "text",
      placeholder: "Application Update: {{position}}",
      required: true,
      fullWidth: true,
    },
    {
      name: "variables",
      label: "Template Variables",
      type: "chips-readonly",
      fullWidth: true,
      hideOnCreate: true,
    },
    {
      name: "body",
      label: "Email Content",
      type: "richtext",
      placeholder: "Write your email content here...",
      required: true,
      fullWidth: true,
    },
  ];

  const handleCopy = (template) => {
    navigator.clipboard.writeText(template.body);
    setCopiedId(template.id);
    toast.success("Template copied to clipboard");
    setTimeout(() => setCopiedId(null), 2000);
  };

  const renderItem = (template, onEdit, onDelete) => (
    <div className="flex flex-col w-full group">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 bg-slate-50 border border-slate-100 rounded-2xl flex items-center justify-center text-subidha-blue group-hover:bg-subidha-blue group-hover:text-white transition-all duration-300">
            <Mail size={24} />
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight">
                {template.name}
              </h3>
              <span className="text-[9px] font-black px-2 py-0.5 rounded-md bg-blue-50 text-subidha-blue border border-blue-100 uppercase tracking-tighter">
                {template.category}
              </span>
            </div>
            <p className="text-[11px] font-bold text-slate-400 mt-1 truncate max-w-[300px]">
              Sub: {template.subject}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-1">
          <button
            onClick={() => handleCopy(template)}
            className="p-2.5 text-slate-400 hover:text-accent-green hover:bg-green-50 rounded-xl transition-all"
          >
            {copiedId === template.id ? (
              <Check size={16} />
            ) : (
              <Copy size={16} />
            )}
          </button>
          {onEdit && (
            <button
              onClick={() => onEdit(template)}
              className="p-2.5 text-slate-400 hover:text-subidha-blue hover:bg-blue-50 rounded-xl transition-all"
            >
              <Edit2 size={16} />
            </button>
          )}
          {onDelete && (
            <button
              onClick={() => onDelete(template)}
              className="p-2.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Preview Box */}
      <div className="bg-slate-50/50 border border-slate-100 rounded-xl p-3 font-mono text-[10px] text-slate-500 line-clamp-2 whitespace-pre-wrap">
        {template.body}
      </div>

      {/* Variables */}
      {template.variables?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {template.variables.map((v) => (
            <span
              key={v}
              className="text-[10px] font-black px-2 py-0.5 rounded-md bg-accent-green/10 text-green-700 border border-accent-green/20 font-mono"
            >
              {`{{${v}}}`}
            </span>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <CrudManager
      title="Email Templates"
      apiEndpoint="/email-templates" // Update with your actual endpoint
      fields={getFields()}
      renderItem={renderItem}
      onClose={onClose}
      icon={Mail}
      emptyMessage="No templates found. Save time by creating one!"
    />
  );
};

export default EmailTemplateManager;
